import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import toast from "react-hot-toast";
import PatientNavbar from "./PatientNavbar";
import { Home, Users, Calendar, User, LogOut } from "lucide-react";

const PatientLayout = ({ children }) => {
    const [user] = useAuthState(auth);
    const [firstName, setFirstName] = useState("Patient");
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchName = async () => {
            if (user) {
                try {
                    // Check auth profile first
                    const authRef = doc(db, "patients_auth", user.uid);
                    const authSnap = await getDoc(authRef);

                    if (authSnap.exists()) {
                        const fullName = authSnap.data().fullName || "";
                        setFirstName(fullName.split(" ")[0]); // Get first name
                        return;
                    }
                } catch (e) {
                    console.error("Error fetching name for layout", e);
                }
            }
        };
        fetchName();
    }, [user]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            toast.success("Logged out successfully");
            navigate("/");
        } catch (err) {
            console.error("Error logging out:", err);
            toast.error("Failed to log out");
        }
    };

    const isActive = (path) => location.pathname === path;

    return (
        <div className="min-h-screen bg-slate-50 relative">
            {/* Background Blobs */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] rounded-full bg-gradient-to-br from-teal-200/40 to-emerald-200/40 blur-[100px] opacity-70 animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-100/60 blur-[100px] opacity-60"></div>
            </div>

            {/* Desktop Navbar - Hidden on Mobile */}
            <div className="hidden md:block relative z-20">
                <PatientNavbar />
            </div>

            {/* Mobile Navbar - Hidden on Desktop */}
            <div className="md:hidden bg-white/90 backdrop-blur-md sticky top-0 z-40 px-4 h-16 border-b border-gray-100 flex items-center justify-between shadow-sm">
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => navigate('/')}
                >
                    <img src="/logo-green.png" alt="PulseNote" className="w-9 h-9" />
                    <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                        PulseNote
                    </span>
                </div>
                <button
                    onClick={handleLogout}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                    title="Logout"
                >
                    <LogOut className="w-5 h-5" />
                </button>
            </div>

            {/* Main Content */}
            <div className="relative z-10 pb-24 md:pb-8">
                {/* Mobile Greeting - Only visible on detailed pages if needed, or always? 
                    Let's show it always as a header for the content. */}
                <div className="md:hidden px-6 pt-6 pb-2">
                    <h1 className="text-xl font-bold text-gray-800">Welcome back, {firstName}</h1>
                    <p className="text-xs text-gray-500">
                        {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                </div>
                {children}
            </div>

            {/* Mobile Bottom Nav - Hidden on Desktop */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-2 pb-safe flex justify-between items-center z-50 md:hidden shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                <button
                    onClick={() => navigate('/patient/portal')}
                    className={`flex flex-col items-center gap-1 p-2 transition-colors ${isActive('/patient/portal') ? 'text-teal-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    <Home className="w-6 h-6" strokeWidth={isActive('/patient/portal') ? 2.5 : 2} />
                    <span className="text-[10px] font-medium">Home</span>
                </button>

                <button
                    onClick={() => navigate('/patient/family-manager')}
                    className={`flex flex-col items-center gap-1 p-2 transition-colors ${isActive('/patient/family-manager') ? 'text-teal-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    <Users className="w-6 h-6" strokeWidth={isActive('/patient/family-manager') ? 2.5 : 2} />
                    <span className="text-[10px] font-medium">Family</span>
                </button>

                <button
                    onClick={() => navigate('/patient/book')}
                    className={`flex flex-col items-center gap-1 p-2 transition-colors ${isActive('/patient/book') ? 'text-teal-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    <Calendar className="w-6 h-6" strokeWidth={isActive('/patient/book') ? 2.5 : 2} />
                    <span className="text-[10px] font-medium">Book</span>
                </button>

                <button
                    onClick={() => navigate('/patient/profile')}
                    className={`flex flex-col items-center gap-1 p-2 transition-colors ${isActive('/patient/profile') ? 'text-teal-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                    <User className="w-6 h-6" strokeWidth={isActive('/patient/profile') ? 2.5 : 2} />
                    <span className="text-[10px] font-medium">Profile</span>
                </button>
            </div>
        </div>
    );
};

export default PatientLayout;
